import {
  createContext,
  createElement,
  useContext,
  useMemo,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type CSSProperties,
} from "react";
import type { Components, ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import { unitMarker } from "@/modules/publicQuote/components/pageBreaks";
import { EmptyTextPlaceholder } from "./EmptyTextPlaceholder";
import { MarkdownProse } from "./MarkdownProse";
import { PROSE_INDENT_CLASS } from "./proseIndent";
import { resolveLineField } from "./lineFields";
import { useLineScope } from "./lineScopeContext";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import {
  FIELD_PLACEHOLDER_CHIP_CLASS,
  quoteFieldLabel,
  resolveQuoteField,
} from "./quoteFields";
import { remarkFieldTokens } from "./remarkFieldTokens";

/**
 * Prose styling every `<fp-text>` block starts from. The app root sets
 * `font-synthesis: none` and the document fonts ship no italic face, so the
 * block opts synthesized oblique back in - without it an italic run renders
 * as plain text.
 */
const FP_TEXT_CLASS = `prose prose-sm max-w-none [font-synthesis:style] [&_*]:text-current [&_li::marker]:text-current [&_ul_ul]:list-[circle] ${PROSE_INDENT_CLASS}`;

/**
 * Type scale the empty box shares with the rendered prose, so clearing a block
 * does not resize its ghost hint - and so the edit session, which copies the
 * block's computed font, opens at the same size either way.
 */
const FP_TEXT_EMPTY_CLASS = "prose prose-sm max-w-none";

/**
 * The slice of an authored `<fp-text>` body one rendered page fragment covers.
 *
 * A fragmentable block holds exactly one such body, so the window is supplied
 * per block rather than keyed: whichever `<fp-text>` reads it is the one it
 * describes.
 */
export interface FpTextUnitWindow {
  /** The block's first text unit, in the enclosing section's unit space. */
  baseUnitIndex: number;
  /** The first text unit this fragment renders, relative to the block. */
  startUnit: number;
  /** One past the last text unit this fragment renders, relative to the block. */
  endUnit: number;
}

/** The text-unit window the enclosing fragment imposes, or null when whole. */
export const FpTextUnitWindowContext = createContext<FpTextUnitWindow | null>(
  null,
);

/**
 * Atomic units of an authored `<fp-text>` body, for fragment pagination.
 *
 * A long prose block - the 25-item clarification list of a proposal, say - must
 * not move to the next page as one lump when it fails to fit under its heading.
 * The fragment planner instead treats the body's leaf markdown blocks as units
 * and breaks pages where a unit starts. Blockquotes are containers, not units:
 * authored documents store indentation as nested blockquotes, so one blockquote
 * can hold a near-page of content, and the units are the paragraphs and list
 * items inside it. A list item stays atomic - its nested sub-content is glued
 * to it.
 *
 * A unit's `start` is the start of its source line, so a window sliced there is
 * itself valid markdown: the `> > >` prefixes of blockquote nesting survive and
 * the continuation renders at the same indent, and an ordered list sliced at
 * item 13 keeps its numbering because markdown honours the first item's number.
 * `markerStart` is the node's own offset - past those prefixes - which is where
 * the rendered element's source position points.
 *
 * The same parser stack the renderer uses (remark-parse + remark-gfm) produces
 * the offsets, so a unit's start always lands on an element the rendered
 * markdown actually has.
 */

/** One atomic text unit: where it is in the markdown source. */
export interface TextUnit {
  /** Offset of the unit's source line start - where a window slices. */
  start: number;
  /** Offset of the unit's own first character - where its element renders. */
  markerStart: number;
  /** Offset one past the unit's last character. */
  end: number;
}

/** The mdast shape this module reads: typed nodes with source positions. */
interface PositionedNode {
  type: string;
  children?: PositionedNode[];
  position?: {
    start: { offset?: number };
    end: { offset?: number };
  };
}

const unitParser = unified().use(remarkParse).use(remarkGfm);

/**
 * Splits a markdown body into its atomic pagination units: its leaf blocks,
 * with blockquotes recursed into and lists contributing one unit per item.
 * @param markdown - The dedented markdown source
 * @returns The units, in source order
 */
export function parseTextUnits(markdown: string): TextUnit[] {
  const root = unitParser.parse(markdown) as PositionedNode;
  return collectUnits(root.children ?? []).map((unit) => ({
    ...unit,
    start: lineStart(markdown, unit.markerStart),
  }));
}

/**
 * The units of a run of sibling blocks: blockquotes defer to the units inside
 * them, a list is one unit per top-level item, and any other block is one unit.
 * @param nodes - The sibling mdast nodes, in source order
 * @returns The units, without their line-aligned starts yet
 */
function collectUnits(nodes: PositionedNode[]): Omit<TextUnit, "start">[] {
  return nodes.flatMap((node) => {
    if (node.type === "blockquote") {
      const inner = collectUnits(node.children ?? []);
      if (inner.length > 0) {
        return inner;
      }
    }
    if (node.type === "list") {
      return (node.children ?? []).flatMap(nodeUnit);
    }
    return nodeUnit(node);
  });
}

/**
 * One node's unit, or none when the parser recorded no offsets for it.
 * @param node - The leaf block or list item
 * @returns The node's unit range
 */
function nodeUnit(node: PositionedNode): Omit<TextUnit, "start">[] {
  const markerStart = node.position?.start.offset;
  const end = node.position?.end.offset;
  if (markerStart === undefined || end === undefined) {
    return [];
  }
  return [{ markerStart, end }];
}

/**
 * The offset of the start of the line an offset falls on.
 * @param source - The markdown source
 * @param offset - The offset whose line start is wanted
 * @returns The line-start offset
 */
function lineStart(source: string, offset: number): number {
  const newline = source.lastIndexOf("\n", offset - 1);
  return newline === -1 ? 0 : newline + 1;
}

/**
 * Strips the common leading indentation off an authored markdown body, so a
 * block indented inside its enclosing HTML is not read as a code block. Only
 * spaces and tabs count as indentation: a leading non-breaking space is the
 * author's own indent and has to survive.
 * @param body - The element's raw text content
 * @returns The dedented markdown
 */
export function dedentAuthoredBody(body: string): string {
  const lines = body
    .replace(/^\n+/, "")
    .replace(/[ \t\n]+$/, "")
    .split("\n");
  const indents = lines
    .filter((line) => line.trim() !== "")
    .map((line) => line.length - line.replace(/^[ \t]+/, "").length);
  const common = indents.length === 0 ? 0 : Math.min(...indents);
  return lines.map((line) => line.slice(common)).join("\n");
}

interface FpTextProps {
  /** The bound markdown field, or null for an authored body. */
  source: string | null;
  /** The element's authored markdown body, used only when `source` is null. */
  content: string;
  style?: CSSProperties;
  "data-fp-node"?: string;
}

/**
 * Renders `<fp-text>`: all prose in the proposed fp-only vocabulary. The body is
 * markdown with inline `{{ source }}` tokens; an optional `source` binds a
 * markdown field (quote scope, or line scope inside a table) instead. An empty
 * body still draws its box when the block carries container props, and in the
 * editor it always draws one - carrying the ghost hint - so an author who
 * clears a text block can still click it to edit or delete it. An unresolved
 * field is a placeholder chip only in the editor: a printed document shows the
 * reader nothing rather than an authoring marker.
 * @param props - The optional field source, authored body, and container style
 * @returns The rendered prose block, its empty box, or a placeholder chip
 */
export function FpText({
  source,
  content,
  style,
  "data-fp-node": dataFpNode,
}: FpTextProps) {
  const { quote, editable } = useQuoteDocumentContext();
  const scope = useLineScope();
  const unitWindow = useContext(FpTextUnitWindowContext);
  const fieldTokenPlugins = useMemo(
    () => [
      remarkFieldTokens((tokenSource) => {
        const resolved = resolveQuoteField(quote, tokenSource);
        if (resolved !== null && resolved !== "") {
          return {
            value: resolved,
            title: editable
              ? (quoteFieldLabel(tokenSource) ?? undefined)
              : undefined,
          };
        }
        return editable
          ? { placeholderLabel: quoteFieldLabel(tokenSource) ?? tokenSource }
          : { value: "" };
      }),
    ],
    [quote, editable],
  );

  const boundValue =
    source === null
      ? null
      : ((scope === null
          ? null
          : resolveLineField(quote, scope, source)?.value) ??
        resolveQuoteField(quote, source));
  const markdown = source === null ? dedentAuthoredBody(content) : boundValue;

  if (markdown === null || markdown === "") {
    if (source !== null && editable) {
      return (
        <span
          className={FIELD_PLACEHOLDER_CHIP_CLASS}
          data-fp-node={dataFpNode}
        >
          {`⟨${quoteFieldLabel(source) ?? source}⟩`}
        </span>
      );
    }
    if (!editable && !hasContainerStyle(style)) {
      return null;
    }
    return (
      <div
        className={FP_TEXT_EMPTY_CLASS}
        style={style}
        data-fp-node={dataFpNode}
      >
        <EmptyTextPlaceholder />
      </div>
    );
  }

  if (unitWindow !== null && source === null) {
    return (
      <WindowedFpText
        markdown={markdown}
        window={unitWindow}
        extraRemarkPlugins={fieldTokenPlugins}
        style={style}
        data-fp-node={dataFpNode}
      />
    );
  }

  return (
    <MarkdownProse
      markdown={markdown}
      extraRemarkPlugins={fieldTokenPlugins}
      className={FP_TEXT_CLASS}
      style={style}
      data-fp-node={dataFpNode}
    />
  );
}

/** The block-level tags a text unit can start on, overridden to carry markers. */
const UNIT_BLOCK_TAGS = [
  "p",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "hr",
  "table",
] as const;

/**
 * Renders the window's slice of an authored body: a verbatim cut of the
 * markdown from the first unit's start to the last unit's end, so a sliced
 * ordered list keeps its numbering and inter-block spacing survives inside the
 * window. Each unit's first element is stamped with its `data-fp-unit` marker
 * by matching the rendered nodes' source offsets against the unit offsets.
 * @param props - The full body, the unit window, and the pass-through props
 * @returns The rendered slice, or null for an empty window
 */
function WindowedFpText({
  markdown,
  window,
  extraRemarkPlugins,
  style,
  "data-fp-node": dataFpNode,
}: {
  markdown: string;
  window: FpTextUnitWindow;
  extraRemarkPlugins: ComponentProps<
    typeof MarkdownProse
  >["extraRemarkPlugins"];
  style?: CSSProperties;
  "data-fp-node"?: string;
}) {
  const units = useMemo(() => parseTextUnits(markdown), [markdown]);
  const startUnit = Math.max(window.startUnit, 0);
  const endUnit = Math.min(window.endUnit, units.length);
  const slice =
    startUnit >= endUnit
      ? ""
      : markdown.slice(units[startUnit].start, units[endUnit - 1].end);
  const components = useMemo(
    () =>
      slice === ""
        ? null
        : unitMarkerComponents(
            slice,
            endUnit - startUnit,
            window.baseUnitIndex + startUnit,
          ),
    [slice, startUnit, endUnit, window.baseUnitIndex],
  );
  if (slice === "" || components === null) {
    return null;
  }
  return (
    <MarkdownProse
      markdown={slice}
      data-fp-text-start={units[startUnit].start}
      data-fp-text-end={units[endUnit - 1].end}
      extraRemarkPlugins={extraRemarkPlugins}
      components={components}
      className={FP_TEXT_CLASS}
      style={style}
      data-fp-node={dataFpNode}
    />
  );
}

/**
 * Component overrides stamping `data-fp-unit` on each unit's first element.
 *
 * The slice's own re-parse supplies the offsets: because a window is cut at
 * line starts, it re-parses to the same units the full body holds there, and
 * each unit's `markerStart` is exactly where its rendered element's source
 * position points. A count mismatch (a slice the parser reads differently)
 * stamps no markers at all - a missing marker only coarsens where pages can
 * break, while a misplaced one would corrupt the measured boundaries.
 * @param slice - The window's markdown source
 * @param unitCount - The number of units the window is expected to hold
 * @param firstUnitIndex - The window's first unit in the section's unit space
 * @returns The react-markdown component overrides
 */
function unitMarkerComponents(
  slice: string,
  unitCount: number,
  firstUnitIndex: number,
): Components {
  const sliceUnits = parseTextUnits(slice);
  const offsets = new Map<number, number>();
  if (sliceUnits.length === unitCount) {
    sliceUnits.forEach((unit: TextUnit, index: number) => {
      offsets.set(unit.markerStart, firstUnitIndex + index);
    });
  }
  return Object.fromEntries(
    UNIT_BLOCK_TAGS.map((tag) => [tag, markedBlock(tag, offsets)]),
  ) as Components;
}

/**
 * A block-tag override rendering its element unchanged, plus the unit marker
 * when the element's source offset opens a unit.
 * @param tag - The HTML tag to render
 * @param offsets - Window-relative source offsets mapped to section unit indices
 * @returns The component override
 */
function markedBlock<Tag extends (typeof UNIT_BLOCK_TAGS)[number]>(
  tag: Tag,
  offsets: Map<number, number>,
) {
  return function MarkedBlock({
    node,
    ...rest
  }: ComponentPropsWithoutRef<Tag> & ExtraProps) {
    const offset = node?.position?.start.offset;
    const index = offset === undefined ? null : (offsets.get(offset) ?? null);
    return createElement(tag, { ...rest, ...unitMarker(index) });
  };
}

/**
 * Whether the block carries container props of its own, which is what makes an
 * empty body still worth drawing: a bordered, padded, or sized box is content.
 * @param style - The block's container style, or undefined when it has none
 * @returns True when at least one container property was authored
 */
function hasContainerStyle(style: CSSProperties | undefined): boolean {
  return style !== undefined && Object.keys(style).length > 0;
}
